import { useEffect, useState } from "react";
import { api, type PostKind } from "../lib/api";
import { useI18n } from "../lib/i18n";

type Notice = { kind: PostKind; id: string; title: string; undo: boolean; expires: number };
const NOTICE_KEY = "post-lifecycle-notice";
const NOTICE_EVENT = "post-lifecycle-notice";

export function announcePostClosed(kind: PostKind, id: string, title: string, undo: boolean) {
  const notice: Notice = { kind, id, title, undo, expires: Date.now() + 30_000 };
  try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice)); } catch { /* Reopen remains available if browser storage is disabled. */ }
  window.dispatchEvent(new Event(NOTICE_EVENT));
}

function readNotice(): Notice | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(NOTICE_KEY) || "null");
    if (!value || typeof value !== "object" || !("kind" in value) || !("id" in value) || !("title" in value) || !("undo" in value) || !("expires" in value)) return null;
    if (typeof value.kind !== "string" || !["job", "service", "project", "rfq", "company", "item"].includes(value.kind) || typeof value.id !== "string" || typeof value.title !== "string" || typeof value.undo !== "boolean" || typeof value.expires !== "number" || value.expires < Date.now()) return null;
    return { kind: value.kind as PostKind, id: value.id, title: value.title, undo: value.undo, expires: value.expires };
  } catch { return null; }
}

export function PostLifecycleNotice() {
  const { t } = useI18n();
  const [notice, setNotice] = useState(readNotice);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const update = () => { setNotice(readNotice()); setError(""); };
    window.addEventListener(NOTICE_EVENT, update);
    return () => window.removeEventListener(NOTICE_EVENT, update);
  }, []);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => { sessionStorage.removeItem(NOTICE_KEY); setNotice(null); }, Math.max(0, notice.expires - Date.now()));
    return () => window.clearTimeout(timer);
  }, [notice]);
  if (!notice) return null;
  return <div role={error ? "alert" : "status"} className="fixed bottom-20 left-4 right-4 z-[60] mx-auto max-w-xl rounded-xl bg-navy p-4 text-sm text-white shadow-lift">
    {error || `${notice.title}: ${t("post.owner.closedNotice")}`}
    {notice.undo && <button type="button" disabled={busy} className="ml-3 font-bold text-brand-gold underline disabled:opacity-50" onClick={() => {
      setBusy(true); setError("");
      void api.reopenPost(notice.kind, notice.id).then(() => {
        sessionStorage.removeItem(NOTICE_KEY); window.location.reload();
      }).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : t("post.owner.reopenError"))).finally(() => setBusy(false));
    }}>{t("post.owner.undo")}</button>}
    <button type="button" className="ml-3 underline" onClick={() => { sessionStorage.removeItem(NOTICE_KEY); setNotice(null); }}>{t("common.dismiss", "Dismiss")}</button>
  </div>;
}
